'use client';
// Site header of the v9 design: transparent over the dark home hero, frosted once scrolled; paper on every other page.
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { useEffect, useRef, useState, useTransition } from 'react';
import { useTranslations } from 'next-intl';
import { useAuthStore } from '@/stores/authStore';
import { getLocalizedPath } from '@/lib/getLocalizedPath';
import { useAppHandoff } from './AppHandoff';
import { ChevronDown, Close, MenuIcon, Sparkle } from './icons';
import { cx, useEscape, useLang, useScrollLock } from './ui';

// The internal path carries the locale ('/ar' for the Arabic root on the first render, '/en/...'); both are stripped
// so the header and HeaderFooterWrapper agree on what the home is.
function stripLocale(pathname: string) {
  return pathname.replace(/^\/(ar|en)(?=\/|$)/, '') || '/';
}

export function LangToggle({ big, className }: { big?: boolean; className?: string }) {
  const { locale } = useLang();
  const router = useRouter();
  const pathname = usePathname();
  const [, startTransition] = useTransition();
  const go = (l: 'ar' | 'en') => {
    if (l === locale) return;
    const rest = stripLocale(pathname);
    const qs = typeof window !== 'undefined' ? window.location.search + window.location.hash : '';
    startTransition(() => router.replace(getLocalizedPath(l, rest) + qs));
  };
  const btn = (l: 'ar' | 'en', label: string) => (
    <button
      type="button"
      onClick={() => go(l)}
      aria-pressed={locale === l}
      className={cx('min-h-[44px] transition-opacity', big ? 'px-3 text-[18px]' : 'min-w-[40px] max-lg:min-w-[34px]', locale === l ? 'opacity-100' : 'opacity-55 hover:opacity-80')}
    >
      {label}
    </button>
  );
  return (
    <div dir="ltr" role="group" aria-label="Language" className={cx('flex items-center font-bold', big ? 'text-[18px]' : 'text-[14px]', className)}>
      {btn('ar', big ? 'العربية' : 'AR')}
      <i className="h-4 w-px bg-current opacity-35" />
      {btn('en', big ? 'English' : 'EN')}
    </div>
  );
}

function AccountMenu({ ink }: { ink: boolean }) {
  const t = useTranslations('v9.header');
  const { locale } = useLang();
  const { isAuthenticated, user, logout } = useAuthStore();
  const { openApp } = useAppHandoff();
  const [mounted, setMounted] = useState(false);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => setMounted(true), []);
  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);
  useEscape(open ? () => setOpen(false) : null);
  const L = (p: string) => getLocalizedPath(locale, p);

  if (!mounted) return <span className="h-11 w-11" />;
  if (!isAuthenticated) {
    return (
      <Link href={L('/auth/signin')} className="hidden min-h-[44px] items-center px-1.5 text-[15px] font-bold opacity-90 transition-opacity hover:opacity-100 lg:inline-flex">
        {t('signin')}
      </Link>
    );
  }
  const initial = (user?.first_name || user?.phone || 'M').trim().charAt(0).toUpperCase();
  // Bookings stay on the website; wallet, cashback, favorites, messages, reviews and the profile live in the app.
  const links: [string, string][] = [['/orders/current', t('orders')]];
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('account')}
        className={cx('flex min-h-[44px] items-center gap-1.5 rounded-full ps-1 pe-2 transition-colors', ink ? 'hover:bg-white/10' : 'hover:bg-deep/[.06]')}
      >
        <span className={cx('grid h-9 w-9 place-items-center rounded-full text-[15px] font-extrabold', ink ? 'bg-sun text-deep' : 'bg-plum text-white')}>{initial}</span>
        <ChevronDown className="h-4 w-4 opacity-70" />
      </button>
      {open && (
        <div role="menu" className="absolute end-0 top-[calc(100%+8px)] z-[60] w-60 overflow-hidden rounded-[22px] bg-white p-2 text-deep shadow-[0_30px_60px_-30px_rgba(57,22,77,.6),0_0_0_1px_rgba(57,22,77,.06)] animate-[v9fade_.2s_ease]">
          <p className="truncate px-3 pb-2 pt-1.5 text-[13px] font-bold text-deep/60">{user?.first_name ? `${user.first_name} ${user.last_name || ''}` : user?.phone}</p>
          {links.map(([href, label]) => (
            <Link key={href} role="menuitem" href={L(href)} onClick={() => setOpen(false)} className="block rounded-[14px] px-3 py-2.5 text-[15px] font-bold hover:bg-panel">
              {label}
            </Link>
          ))}
          <button role="menuitem" type="button" onClick={() => { setOpen(false); openApp(); }} className="block w-full rounded-[14px] px-3 py-2.5 text-start hover:bg-panel">
            <b className="block text-[15px] font-bold">{t('inApp')}</b>
            <small className="block font-body text-[12.5px] leading-[1.6] text-deep/60">{t('inAppSub')}</small>
          </button>
          <button role="menuitem" type="button" onClick={() => { setOpen(false); logout(); }} className="mt-1 block w-full rounded-[14px] px-3 py-2.5 text-start text-[15px] font-bold text-bad hover:bg-bad-soft">
            {t('logout')}
          </button>
        </div>
      )}
    </div>
  );
}

export default function SiteHeader() {
  const t = useTranslations('v9.header');
  const tc = useTranslations('v9.common');
  const { locale } = useLang();
  const pathname = usePathname();
  const { openApp } = useAppHandoff();
  const { isAuthenticated, logout } = useAuthStore();
  const rest = stripLocale(pathname);
  const isHome = rest === '/';
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  useScrollLock(menuOpen);
  useEscape(menuOpen ? () => setMenuOpen(false) : null);
  useEffect(() => setMenuOpen(false), [pathname]);

  // Frosted once scrolled (the design's 12px).
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // On the home page the header is white over the dark hero and turns paper once the hero has scrolled under
  // it: an observer with the header's height as its margin, re-armed when the hero mounts after the header.
  const [pastHero, setPastHero] = useState(false);
  useEffect(() => {
    if (!isHome) { setPastHero(false); return; }
    let io: IntersectionObserver | null = null;
    let retry = 0;
    const arm = () => {
      const hero = document.getElementById('hero');
      if (!hero) { retry = window.setTimeout(arm, 150); return; }
      const hdr = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--hdr')) || 84;
      io = new IntersectionObserver(([e]) => setPastHero(!e.isIntersecting), { rootMargin: `-${hdr}px 0px 0px 0px` });
      io.observe(hero);
    };
    arm();
    return () => { io?.disconnect(); window.clearTimeout(retry); };
  }, [isHome, pathname]);

  const ink = isHome && !pastHero;
  const solid = !isHome || scrolled;
  const L = (p: string) => getLocalizedPath(locale, p);
  // Main menu (SEO item 13): book a restaurant, occasions, how it works, join as a partner. The blog, the add-ons and
  // the city pages are in the footer.
  const nav: { href: string; label: string; main?: boolean; match?: string }[] = [
    { href: L('/reserve'), label: t('bookVenue'), main: true, match: '/reserve' },
    { href: L('/#occasions'), label: t('occasions'), match: '/occasions' },
    { href: L('/#how-it-works'), label: t('how') },
    { href: L('/join-as-vendor'), label: t('partner'), match: '/join-as-vendor' },
  ];

  return (
    <>
      <a href="#main" className="fixed start-2 top-2 z-[99] -translate-y-[200%] rounded-xl bg-sun px-4 py-2.5 font-bold text-deep focus:translate-y-0">{tc('skip')}</a>
      <header
        className={cx(
          'fixed inset-x-0 top-0 z-50 flex h-[var(--hdr)] items-center transition-[background-color,box-shadow,color] duration-[400ms]',
          ink ? 'text-white' : 'text-deep',
          solid && (ink
            ? 'bg-[rgba(46,15,69,.8)] shadow-[0_1px_0_rgba(255,255,255,.08)] backdrop-blur-[14px]'
            : 'bg-[rgba(251,248,252,.86)] shadow-[0_1px_0_rgba(57,22,77,.08)] backdrop-blur-[14px]'),
        )}
      >
        <div className="v9-wrap--wide mx-auto flex items-center gap-[clamp(8px,2vw,28px)]">
          <Link href={L('/')} aria-label="Mila" className="relative block h-[46px] flex-none max-lg:h-10" style={{ aspectRatio: '900/381' }}>
            <img src="/v9/logo-white.webp" alt="Mila" width={900} height={381} className={cx('absolute inset-0 h-full w-full transition-opacity duration-300', ink ? 'opacity-100' : 'opacity-0')} />
            <img src="/v9/logo-plum.webp" alt="" aria-hidden="true" width={900} height={381} className={cx('absolute inset-0 h-full w-full transition-opacity duration-300', ink ? 'opacity-0' : 'opacity-100')} />
          </Link>
          <nav aria-label="Mila" className="ms-auto hidden gap-[clamp(8px,1.4vw,24px)] text-[15.5px] font-bold lg:flex">
            {nav.map((n) => {
              const on = !!n.match && rest.startsWith(n.match);
              return (
                <Link key={n.href} href={n.href} className={cx('group relative flex items-center gap-2 px-1.5 py-3 transition-opacity', on ? 'opacity-100' : 'opacity-[.86] hover:opacity-100')}>
                  {n.main && <span className="h-2 w-2 rounded-full bg-sun" />}
                  {n.label}
                  <span className={cx('absolute inset-x-1.5 bottom-1.5 h-0.5 origin-center rounded bg-current transition-transform duration-300 ease-mila', on ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100')} />
                </Link>
              );
            })}
          </nav>
          <div className="ms-auto flex items-center gap-[clamp(6px,1.2vw,16px)] lg:ms-0">
            {/* Phones switch language from the menu, as in the design. */}
            <LangToggle className="max-lg:hidden" />
            <AccountMenu ink={ink} />
            <button
              type="button"
              onClick={() => openApp()}
              className={cx(
                'inline-flex min-h-[46px] items-center gap-2.5 whitespace-nowrap rounded-full px-[22px] text-[15.5px] font-extrabold transition-shadow max-lg:min-h-[44px] max-lg:px-4 max-lg:text-[14.5px]',
                ink ? 'bg-sun text-deep shadow-sun' : 'bg-deep text-white shadow-[0_10px_30px_-14px_rgba(57,22,77,.8)]',
              )}
            >
              <Sparkle className={cx('h-[13px] w-[13px]', !ink && 'text-sun')} />
              {tc('getApp')}
            </button>
            <button type="button" onClick={() => setMenuOpen(true)} aria-haspopup="dialog" aria-expanded={menuOpen} aria-label={tc('menu')} className={cx('grid h-11 w-11 place-items-center rounded-full lg:hidden', ink ? 'bg-white/[.12] text-white shadow-[inset_0_0_0_1px_rgba(240,213,140,.75)]' : 'bg-white text-plum shadow-[inset_0_0_0_1px_rgba(85,39,109,.35)]')}>
              <MenuIcon className="h-6 w-6" />
            </button>
          </div>
        </div>
      </header>

      {menuOpen && (
        <div role="dialog" aria-modal="true" aria-label={tc('menu')} className="fixed inset-0 z-[115] flex flex-col items-center justify-center gap-6 overflow-y-auto bg-[rgba(46,15,69,.97)] p-6 text-white animate-[v9fade_.2s_ease]">
          <button type="button" onClick={() => setMenuOpen(false)} aria-label={tc('close')} className="absolute end-3 top-[calc(14px+env(safe-area-inset-top,0px))] grid h-11 w-11 place-items-center rounded-full bg-white text-deep">
            <Close className="h-5 w-5" />
          </button>
          <nav aria-label="Mila" className="flex flex-col items-center gap-1 text-center">
            {[{ href: L('/'), label: t('home') }, ...nav, { href: L('/contact-us'), label: t('contact') }].map((n) => (
              <Link key={n.href} href={n.href} onClick={() => setMenuOpen(false)} className="min-h-[52px] px-5 py-1.5 text-[24px] font-extrabold leading-[1.6] active:text-sun">{n.label}</Link>
            ))}
          </nav>
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-1 text-[15px] font-bold text-white/80">
            {isAuthenticated ? (
              <>
                <Link href={L('/orders/current')} onClick={() => setMenuOpen(false)}>{t('orders')}</Link>
                <button type="button" onClick={() => { setMenuOpen(false); openApp(); }}>{t('inApp')}</button>
                <button type="button" onClick={() => { logout(); setMenuOpen(false); }}>{t('logout')}</button>
              </>
            ) : (
              <Link href={L('/auth/signin')} onClick={() => setMenuOpen(false)}>{t('signin')}</Link>
            )}
          </div>
          <button type="button" onClick={() => { setMenuOpen(false); openApp(); }} className="inline-flex min-h-[46px] items-center gap-2.5 rounded-full bg-sun px-6 font-extrabold text-deep">
            <Sparkle className="h-[13px] w-[13px]" />{tc('getApp')}
          </button>
          <LangToggle big />
        </div>
      )}
    </>
  );
}
